import { useLoginStore } from '@/login/stores/useLoginStore';
import { PackageModule } from '@sysarb/sysarb-types/services/v1/instance/packageModule';
import type { ComputedRef } from 'vue';
import { computed } from 'vue';
import { storeToRefs } from 'pinia';

export type UsePackagesReturn = {
    enableJobArchitecture: ComputedRef<boolean>;
    enableAnalysis: ComputedRef<boolean>;
    enablePayEquityAudit: ComputedRef<boolean>;
    enablePayManagement: ComputedRef<boolean>;
    enableDiscussions: ComputedRef<boolean>;
    enableTargets: ComputedRef<boolean>;
    enableInvolvePortalManager: ComputedRef<boolean>;
    enableInvolvePortalEmployee: ComputedRef<boolean>;
    enableInvolveSettingsPage: ComputedRef<boolean>;
    enableInvolveReport: ComputedRef<boolean>;
    payManagementOnly: ComputedRef<boolean>;
    enableSSO: ComputedRef<boolean>;
    enableIntegrations: ComputedRef<boolean>;
    modulesLoading: ComputedRef<boolean>;
    packageName: ComputedRef<string>;
    modulesToString : ComputedRef<string>;
};

export function usePackage(): UsePackagesReturn {
    const { selectedInstance } = storeToRefs(useLoginStore());

    const modules = computed(() => {
        if (selectedInstance.value?.basics?.packages == null) return new Set<PackageModule>();
        return new Set(selectedInstance.value.basics.packages.modules);
    });

    function allowedByPackage(module: PackageModule): boolean {
        if (modules.value.size === 0) {
            console.warn({ cat: 'packages', msg: 'Modules are not loaded yet' });
            return false;
        }
        console.info({ cat: 'packages', msg: module, enabled: modules.value.has(module) });
        return modules.value.has(module);
    }

    const modulesLoading = computed(() => modules.value.size === 0);

    const enableJobArchitecture = computed(() => {
        const enabled = allowedByPackage(PackageModule.JobArchitecture);
        console.info({ cat: 'packages', msg: PackageModule.JobArchitecture, enabled });
        return enabled;
    });

    const enableAnalysis = computed(() => {
        const enabled = allowedByPackage(PackageModule.Analysis);
        console.info({ cat: 'packages', msg: PackageModule.Analysis, enabled });
        return enabled;
    });

    const enablePayEquityAudit = computed(() => {
        const enabled = allowedByPackage(PackageModule.PayEquityAudit);
        console.info({ cat: 'packages', msg: PackageModule.PayEquityAudit, enabled });
        return enabled;
    });

    const enablePayManagement = computed(() => {
        const enabled = allowedByPackage(PackageModule.PayManagement);
        console.info({ cat: 'packages', msg: PackageModule.PayManagement, enabled });
        return enabled;
    });

    const enableDiscussions = computed(() => {
        const enabled = allowedByPackage(PackageModule.Discussions);
        console.info({ cat: 'packages', msg: PackageModule.Discussions, enabled });
        return enabled;
    });

    const enableTargets = computed(() => {
        const enabled = allowedByPackage(PackageModule.Targets);
        console.info({ cat: 'packages', msg: PackageModule.Targets, enabled });
        return enabled;
    });

    const enableInvolvePortalManager = computed(() => {
        const enabled = allowedByPackage(PackageModule.InvolvePortalManager);
        console.info({ cat: 'packages', msg: PackageModule.InvolvePortalManager, enabled });
        return enabled;
    });

    const enableInvolvePortalEmployee = computed(() => {
        const enabled = allowedByPackage(PackageModule.InvolvePortalEmployee);
        console.info({ cat: 'packages', msg: PackageModule.InvolvePortalEmployee, enabled });
        return enabled;
    });

    const enableInvolveSettingsPage = computed(() => {
        const enabled = allowedByPackage(PackageModule.InvolveSettingsPage);
        console.info(PackageModule.InvolveSettingsPage, enabled);
        return enabled;
    });

    const enableInvolveReport = computed(() => {
        const enabled = allowedByPackage(PackageModule.InvolveEmployeeReport);
        console.info(PackageModule.InvolveEmployeeReport, enabled);
        return enabled;
    });

    // Pay Management package: the involve manager portal with pay management, but no employee portal.
    // Employees and managers land on activities and never see the employee dashboard.
    // Legacy customers (pay management without any involve modules) must not match.
    const payManagementOnly = computed(() => enableInvolvePortalManager.value === true && enablePayManagement.value === true && enableInvolvePortalEmployee.value === false);

    const enableSSO = computed(() => {
        const enabled = allowedByPackage(PackageModule.SSO);
        console.info({ cat: 'packages', msg: PackageModule.SSO, enabled });
        return enabled;
    });

    const enableIntegrations = computed(() => {
        const enabled = allowedByPackage(PackageModule.Integrations);
        console.info({ cat: 'packages', msg: PackageModule.Integrations, enabled });
        return enabled;
    });

    const packageName = computed(() => selectedInstance.value?.basics?.packages.type ?? '');
    const modulesToString = computed(() => Array.from(modules.value).join(', '));

    return {
        enableJobArchitecture,
        enableAnalysis,
        enablePayEquityAudit,
        enablePayManagement,
        enableDiscussions,
        enableTargets,
        enableInvolvePortalManager,
        enableInvolvePortalEmployee,
        enableInvolveSettingsPage,
        enableInvolveReport,
        payManagementOnly,
        enableSSO,
        enableIntegrations,
        modulesToString,
        modulesLoading,
        packageName
    };
}
